import { CSSProperties } from 'react';

import {
  FilestackImageData,
  FilestackImageFit,
  FilestackImageQuality,
} from 'api/types/shared.types';
import { Image } from 'components/Image/Image';

import { getFilestackImageAspectRatio } from './getFilestackImageAspectRatio';
import { getFilestackImageFormat } from './getFilestackImageFormat';
import { getFilestackImageProps } from './getFilestackImageProps';

type Props = {
  data: FilestackImageData | undefined;
  alt?: string;
  height?: number;
  maxHeight?: number;
  width?: number;
  maxWidth?: number;
  expandSize?: boolean;
  quality?: FilestackImageQuality;
  objectFit?: 'cover' | 'contain';
  objectPosition?: CSSProperties['objectPosition'];
  aspectRatio?: { width: number; height: number };
  round?: boolean;
  fit?: FilestackImageFit;
  eagerLoad?: boolean;
  'data-qa-id'?: string;
};

export function FilestackImage({
  data,
  alt,
  width: requestedWidth,
  maxWidth,
  height: requestedHeight,
  maxHeight,
  expandSize,
  quality: qualityName,
  objectFit,
  objectPosition,
  aspectRatio: aspectRatioProp,
  round,
  fit,
  eagerLoad,
  'data-qa-id': qaId,
}: Props) {
  const originalWidth = data?.width;
  const originalHeight = data?.height;
  const handle = data?.handle;
  const mimetype = data?.mimetype;

  const quality = qualityName === 'high' ? 100 : 70;
  const format = getFilestackImageFormat(mimetype);

  const srcData = getFilestackImageProps({
    handle,
    mimetype,
    quality,
    format,
    fit,
    originalWidth,
    originalHeight,
    requestedWidth,
    requestedHeight,
    maxWidth,
    maxHeight,
  });

  const aspectRatio =
    aspectRatioProp ||
    getFilestackImageAspectRatio({
      originalWidth,
      originalHeight,
      requestedWidth,
      requestedHeight,
    });

  return (
    <Image
      loadingSrc={srcData?.loadingSrc}
      src={srcData?.src || ''}
      srcSet={srcData?.srcSet}
      width={srcData?.size.width}
      height={srcData?.size.height}
      aspectRatio={aspectRatio}
      expandSize={expandSize}
      objectFit={objectFit}
      objectPosition={objectPosition}
      round={round}
      eagerLoad={eagerLoad}
      data-qa-id={qaId}
      alt={alt || data?.altText || ''}
    />
  );
}
